/**
 * Form Error Handler
 *
 * Application utilities for processing API errors and attaching form field metadata.
 * Uses pure business logic from the domain layer for mappings.
 *
 * @module domains/personnel/application/errors/formErrorHandler
 */

import { isAxiosError, hasApiErrorResponse } from '@personnel/application/errors/typeGuards';
import { extractErrorInfo } from '@personnel/application/errors/errorHandler';
import type { FormFieldError, FormFieldMapping } from '@personnel/domain/errors/formFieldMappings';
import { getFormFieldForError, ROLE_FORM_FIELD_MAPPINGS } from '@personnel/domain/errors/formFieldMappings';

/**
 * Extended error with form field metadata
 */
export interface ErrorWithFormField extends Error {
  formFieldError?: FormFieldError;
}

/**
 * Processes an API error and attaches form field metadata if applicable
 *
 * This function:
 * 1. Extracts error info from Axios response
 * 2. Uses domain mappings to determine if error is field-specific (business logic)
 * 3. Attaches metadata to error object
 *
 * @param error - The error to process
 * @param mappings - Custom error-to-field mappings (defaults to ROLE_FORM_FIELD_MAPPINGS)
 * @returns The same error with formFieldError attached if applicable
 *
 * @example
 * onError: (error: Error) => {
 *   attachFormFieldError(error);
 *   handleError(error);
 * }
 */
export const attachFormFieldError = (
  error: Error,
  mappings: FormFieldMapping[] = ROLE_FORM_FIELD_MAPPINGS,
): ErrorWithFormField => {
  // Guard: Only process Axios errors with API responses
  if (!isAxiosError(error) || !hasApiErrorResponse(error)) {
    return error;
  }

  const statusCode = error.response?.status || 500;
  const apiErrorResponse = error.response?.data;

  if (!apiErrorResponse) {
    return error;
  }

  // Extract error information from API response
  const errorInfo = extractErrorInfo(apiErrorResponse, statusCode);

  // Use domain logic to get form field for this error code
  const fieldName = getFormFieldForError(errorInfo.code, mappings);

  // Attach form field metadata if mapping exists
  if (fieldName) {
    (error as ErrorWithFormField).formFieldError = {
      field: fieldName,
      code: errorInfo.code,
    };
  }

  return error;
};

/**
 * Checks if an error has form field metadata attached
 *
 * Type guard for ErrorWithFormField
 *
 * @param error - The error to check
 * @returns True if the error has form field metadata
 *
 * @example
 * if (hasFormFieldError(error)) {
 *   setFieldError(error.formFieldError.field, t(error.formFieldError.code));
 * }
 */
export const hasFormFieldError = (error: unknown): error is ErrorWithFormField => {
  return (
    error instanceof Error &&
    'formFieldError' in error &&
    typeof (error as ErrorWithFormField).formFieldError === 'object'
  );
};
